import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AIJobStatus, BrandProfileAnalysis } from 'types'
import {
  useCreateBrandProfileAnalysis,
  Props,
  Options,
  Variables,
} from 'hooks/queries/brandProfiles/useCreateBrandProfileAnalysis'

export const fetchAnalysis = async ({
  brandProfileId,
  id,
  workspaceId,
  signal,
}: {
  id: number
  brandProfileId: number
  workspaceId: number
  signal?: AbortSignal
}) => {
  const res = await sendToApi<{ brand_profile_analysis: BrandProfileAnalysis }>(
    `/w/${workspaceId}/brand_profiles/${brandProfileId}/analyses/${id}`,
    { signal },
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }
  return res.data
}

export const useCreateBrandProfileAnalysisSync = (props: Props, options?: Options) => {
  const { brandProfileId } = props
  const { onCreate, ...rest } = options || {}
  const { mutateAsync: createAnalysis } = useCreateBrandProfileAnalysis(props)
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandProfileAnalysis, Error, Variables>(async (vars) => {
    const { prompt, signal } = vars

    let analysis = await createAnalysis({
      prompt,
      signal,
    })

    onCreate?.(analysis)

    let interval = 500

    do {
      const result = await fetchAnalysis({ id: analysis.id, workspaceId, brandProfileId })

      analysis = result.brand_profile_analysis

      if (signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (analysis?.status === AIJobStatus.Failed) {
        throw new Error('Failed analyse brand profile')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(analysis?.status))

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandProfiles'])

    return analysis
  }, rest)
}
